/**
 * Gift Service for Poker Reborn Table Gifts (Throwables)
 *
 * Manages sending and receiving gifts between players at the table.
 * Sends gift commands to the Account service via GL protocol, and
 * handles incoming gift broadcasts from the croupier.
 *
 * Protocol:
 *   Send:    `# account gift <croupier_glid> <target_seat> <gift_id>` → ACCOUNT
 *   Receive: `{ a: "gift", from: N, to: N, gift: "id", tier: N }` ← croupier
 *   Error:   `{ a: "account", reason: "gift_err", error: "..." }` ← ACCOUNT
 *
 * @see tasks/done/T42_TABLE_GIFTS_PLAN.md - Feature plan
 * @see src/types/gifts.ts - Type definitions
 * @see src/config/gifts.ts - Gift catalog
 */

import { EventEmitter } from './EventEmitter';
import { networkService } from './NetworkService';
import { gameEventBus } from '@/effects/events/GameEventBus';
import { tableState, getPlayer } from '@/state/TableState';
import { getGiftById } from '@/config/gifts';
import { GLIDS } from '@/config/network';
import { Commands } from '@/types/protocol';
import type { GiftThrownEvent } from '@/types/gifts';
import { log } from '@/utils/log';

// =============================================================================
// SERVICE EVENTS
// =============================================================================

interface GiftServiceEvents {
  /** A gift was received (from server broadcast or mock) */
  giftReceived: GiftThrownEvent;
  /** Error sending a gift */
  giftError: { message: string };
}

// =============================================================================
// GIFT SERVICE
// =============================================================================

/**
 * Singleton service for table gift interactions.
 *
 * Flow (live):
 * 1. Player taps avatar → GiftPopup opens → selects gift
 * 2. sendGift() → sends `# account gift ...` to ACCOUNT service
 * 3. Server deducts balance, broadcasts `{ a: "gift" }` via croupier
 * 4. handleServerGift() → emits to gameEventBus → Effects Engine plays animation
 *
 * Flow (mock/debug):
 * 1. triggerMockGift() → emits directly to gameEventBus (no server)
 */
export class GiftService extends EventEmitter<GiftServiceEvents> {
  private static instance: GiftService | null = null;

  private constructor() {
    super();
  }

  static getInstance(): GiftService {
    if (!GiftService.instance) {
      GiftService.instance = new GiftService();
    }
    return GiftService.instance;
  }

  // ---------------------------------------------------------------------------
  // SEND GIFT
  // ---------------------------------------------------------------------------

  /**
   * Send a gift to another player via the Account service.
   *
   * Sends `# account gift <croupier> <seat> <giftId>` to ACCOUNT.
   * The server validates, deducts balance, and broadcasts `{ a: "gift" }`
   * to all players at the table. The animation triggers when that
   * broadcast arrives in handleServerGift().
   */
  sendGift(targetSeat: number, giftId: string): void {
    const gift = getGiftById(giftId);
    if (!gift) {
      log.gifts.warning`Gift not found: ${giftId}`;
      this.emit('giftError', { message: `Unknown gift: ${giftId}` });
      return;
    }

    const croupierGlid = tableState.tableId.get();
    if (!croupierGlid) {
      log.gifts.warning`Cannot send gift: not at a table`;
      this.emit('giftError', { message: 'Not at a table' });
      return;
    }

    if (!networkService.isConnected()) {
      log.gifts.warning`Cannot send gift: not connected`;
      this.emit('giftError', { message: 'Not connected' });
      return;
    }

    log.gifts.info`Sending gift: ${gift.emoji} ${gift.name} to seat ${String(targetSeat)}`;

    const command = Commands.giftSend(croupierGlid, targetSeat, giftId);
    networkService.sendCommand(GLIDS.ACCOUNT, command);

    // Track in session events
    gameEventBus.emit('gift:sent', { toSeat: targetSeat, giftId });
  }

  // ---------------------------------------------------------------------------
  // SERVER MESSAGE HANDLERS
  // ---------------------------------------------------------------------------

  /**
   * Handle incoming gift broadcast from croupier.
   * Called by TableController when it receives `{ a: "gift", ... }`.
   *
   * Resolves sender name from table state and triggers the full
   * animation pipeline via gameEventBus.
   */
  handleServerGift(body: Record<string, unknown>): void {
    const serverFromSeat = body['from'] as number | undefined;
    const serverToSeat = body['to'] as number | undefined;
    const giftId = body['gift'] as string | undefined;

    if (serverFromSeat === undefined || serverToSeat === undefined || !giftId) {
      log.gifts.warning`Invalid gift broadcast: missing fields`;
      return;
    }

    const giftDefinition = getGiftById(giftId);
    if (!giftDefinition) {
      log.gifts.warning`Server sent unknown gift: ${giftId}`;
      return;
    }

    // Convert server 1-based seats to client 0-based
    const fromSeat = serverFromSeat - 1;
    const toSeat = serverToSeat - 1;

    // Resolve sender name from table state
    const senderPlayer = getPlayer(fromSeat);
    const senderName = senderPlayer?.profile.name ?? `Player ${String(fromSeat)}`;

    // Check if hero is the receiver
    const heroSeat = tableState.heroSeat.get();
    const isHeroReceiver = heroSeat !== null && toSeat === heroSeat;

    const event: GiftThrownEvent = {
      fromSeat,
      toSeat,
      giftId,
      senderName,
      isHeroReceiver,
    };

    log.gifts.info`Gift received: ${giftDefinition.emoji} from ${senderName} (seat ${String(fromSeat)}) → seat ${String(toSeat)}`;

    gameEventBus.emit('gift:thrown', event);
    this.emit('giftReceived', event);
  }

  /**
   * Handle gift error response from Account service.
   * Called by TableController when it receives `{ a: "account", reason: "gift_err" }`.
   */
  handleGiftError(body: Record<string, unknown>): void {
    const errorCode = (body['error'] as string | undefined) ?? 'unknown';

    const errorMessages: Record<string, string> = {
      not_enough: 'Not enough balance to send this gift',
      cooldown: 'Please wait before sending another gift',
      invalid_gift: 'Invalid gift',
      invalid_seat: 'Invalid target seat',
    };

    const message = errorMessages[errorCode] ?? `Gift error: ${errorCode}`;
    log.gifts.warning`Gift error from server: ${errorCode}`;
    this.emit('giftError', { message });

    // Track in session events
    gameEventBus.emit('gift:error', { error: errorCode, message });
  }

  // ---------------------------------------------------------------------------
  // MOCK / DEBUG
  // ---------------------------------------------------------------------------

  /**
   * Trigger a mock gift for testing — fires the full animation pipeline
   * without going through the server. Use from debug keyboard shortcut.
   */
  triggerMockGift(
    fromSeat: number,
    toSeat: number,
    giftId: string,
  ): void {
    log.gifts.info`[MOCK] Triggering gift: ${giftId} from seat ${String(fromSeat)} → seat ${String(toSeat)}`;

    const senderPlayer = getPlayer(fromSeat);
    const senderName = senderPlayer?.profile.name ?? 'TestPlayer';

    const heroSeat = tableState.heroSeat.get();
    const isHeroReceiver = heroSeat !== null && toSeat === heroSeat;

    const event: GiftThrownEvent = {
      fromSeat,
      toSeat,
      giftId,
      senderName,
      isHeroReceiver,
    };

    gameEventBus.emit('gift:thrown', event);
    this.emit('giftReceived', event);
  }
}

// =============================================================================
// SINGLETON EXPORT
// =============================================================================

export const giftService = GiftService.getInstance();
